import type { VariantProps } from 'class-variance-authority';

import type { RoomCountPickerState, RoomCountStep } from '@elara/client-core/lib/room-count-picker';

import type { buttonVariants } from '#ui/button-variants.js';

import { bookedButtonLabel, roomCountLabel } from '@elara/client-core/lib/room-count-picker';
import { cn } from '@elara/client-core/lib/utils';

import { Button } from '#ui/button.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { Stepper } from '#ui/stepper.js';

export interface RoomCountPickerProps {
  state: RoomCountPickerState;
  /** A step or checkout is in flight: every control waits for it. */
  busy?: boolean;
  /** The step in flight, so plus spins while the hotel is checked and minus while the room is removed. */
  step?: RoomCountStep;
  /** Set while checkout is being prepared, so only Book shows the spinner. */
  booking?: boolean;
  /** Why nothing can be added right now (no trip, offline); disables every add. */
  disabledReason?: string;
  /**
   * The rate is sold out: a red "Sold out" that cannot be pressed takes "Add room"'s place with nothing added,
   * and Book's place once rooms are added.
   */
  soldOut?: boolean;
  onAdd: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  /** Goes to checkout for the added rooms. */
  onBook: () => void;
  /** Opens the booked rooms: the one booking, or a chooser when there are several. */
  onViewBooked: () => void;
  size?: VariantProps<typeof buttonVariants>['size'];
  className?: string;
}

/**
 * The room count control on a rate card, filling its width: a full-width "Add room" until a room of the
 * rate is on the trip, then a minus / count / plus picker and Book splitting the row. Once everything added is
 * booked: "Add another" beside a green Booked ("2 booked" for several); adding starts a new count, and the
 * earlier bookings stop showing on the card until everything added is booked again.
 * Presentational: every state comes in as props so each can be shown in Storybook. The connected
 * version is `RoomCountControl`; the rules for each state live in `room-count-picker.ts`.
 */
export function RoomCountPicker({
  state,
  busy = false,
  step,
  booking = false,
  disabledReason,
  soldOut = false,
  onAdd,
  onIncrement,
  onDecrement,
  onBook,
  onViewBooked,
  size,
  className,
}: RoomCountPickerProps) {
  const addDisabled = busy || !!disabledReason || !state.canIncrement;
  // Plus off because the hotel has no more of the room (or the add cap is reached): the add buttons say so
  // rather than sitting there dimmed with nothing to explain them. Offline or with no trip they keep their
  // label, since rooms may well be left.
  const addLabel = (label: string) => (state.canIncrement ? label : 'No rooms left');
  // The hotel's refusal is a toast (the store's), so a card's buttons stay in line with the others'; only why
  // nothing can be added right now shows here.
  const message = disabledReason;

  if (state.kind === 'none' && soldOut) {
    return (
      <div className={cn('flex flex-col gap-3', className)}>
        <Button variant="destructive" size={size} className="w-full disabled:opacity-100" disabled>
          Sold out
        </Button>
      </div>
    );
  }

  if (state.kind === 'none') {
    return (
      <div className={cn('flex flex-col gap-3', className)}>
        <Button size={size} className="w-full" onClick={onAdd} disabled={addDisabled} isBusy={busy}>
          {addLabel('Add room')}
        </Button>
        {message && <PickerMessage>{message}</PickerMessage>}
      </div>
    );
  }

  // Everything added is booked: no count to step yet. "Add another" starts a new count from one, beside
  // the booking it would sit next to.
  if (state.kind === 'booked') {
    return (
      <div className={cn('flex flex-col gap-3', className)}>
        <div className="grid grid-cols-2 gap-2">
          <Button size={size} onClick={onAdd} disabled={addDisabled} isBusy={busy}>
            {addLabel('Add another')}
          </Button>
          <Button variant="success" size={size} onClick={onViewBooked}>
            <MaterialIcon icon="check_circle" size={IconSizes.sm} />
            <span>{bookedButtonLabel(state.booked)}</span>
          </Button>
        </div>
        {message && <PickerMessage>{message}</PickerMessage>}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="grid grid-cols-2 gap-2">
        <Stepper
          aria-label="Rooms on your trip"
          value={state.added}
          onChange={(next) => (next > state.added ? onIncrement() : onDecrement())}
          min={0}
          max={Number.POSITIVE_INFINITY}
          label={roomCountLabel(state)}
          decrementLabel="Remove a room"
          incrementLabel="Add a room"
          canDecrement={state.canDecrement}
          canIncrement={!disabledReason && state.canIncrement}
          disabled={busy}
          pendingStep={step}
          size={size === 'sm' ? 'sm' : 'default'}
          fullWidth
        />

        {/* A rate that sold out after rooms were added cannot be booked: say so where Book was, as the
            empty picker does. Minus stays, so the rooms can be taken off the trip. */}
        {soldOut ? (
          <Button variant="destructive" size={size} className="disabled:opacity-100" disabled>
            Sold out
          </Button>
        ) : (
          <Button size={size} onClick={onBook} disabled={busy} isBusy={booking}>
            <MaterialIcon icon="shopping_cart_checkout" size={IconSizes.sm} />
            <span>Book</span>
          </Button>
        )}
      </div>

      {message && <PickerMessage>{message}</PickerMessage>}
    </div>
  );
}

function PickerMessage({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-pretty text-muted-foreground">{children}</p>;
}
